'use client'

import { createContext, useContext, useMemo } from 'react'
import { useMachine } from '@xstate/react'
import { queueListMachine } from './machines/queueListMachine'
import type { Queue, QueueAgent, QueueCaller, QueueStats } from './types'

export type QueueListMachineApi = ReturnType<typeof useMachine<typeof queueListMachine>>

const QueueListMachineContext = createContext<QueueListMachineApi | null>(null)

export function QueueListMachineProvider({ children }: { children: React.ReactNode }) {
  const api = useMachine(queueListMachine)
  return <QueueListMachineContext.Provider value={api}>{children}</QueueListMachineContext.Provider>
}

export function useQueueListMachine() {
  const ctx = useContext(QueueListMachineContext)
  if (!ctx) {
    throw new Error('useQueueListMachine must be used within QueueListMachineProvider')
  }
  return ctx
}

export function useQueueListMachineOptional() {
  return useContext(QueueListMachineContext)
}

/**
 * Adapter: returns store-compatible interface.
 */
export function useQueueListStoreCompat() {
  const machineApi = useQueueListMachine()
  return useMemo(() => {
    const [state, send] = machineApi
    const ctx = state.context

    const getState = () => ({
      queues: machineApi[0].context.queues,
      selectedQueue: machineApi[0].context.selectedQueue,
      isLoading: machineApi[0].context.isLoading,
    })

    return {
      queues: ctx.queues,
      selectedQueue: ctx.selectedQueue,
      isLoading: ctx.isLoading,
      getState,
      setQueues: (queues: Queue[]) => send({ type: 'SET_QUEUES', queues }),
      setSelectedQueue: (queue: Queue | null) => send({ type: 'SET_SELECTED_QUEUE', queue }),
      updateQueue: (id: string, updates: Partial<Queue>) =>
        send({ type: 'UPDATE_QUEUE', id, updates }),
      updateQueueStats: (id: string, stats: QueueStats) =>
        send({ type: 'UPDATE_QUEUE_STATS', id, stats }),
      addAgent: (queueId: string, agent: QueueAgent) => send({ type: 'ADD_AGENT', queueId, agent }),
      updateAgent: (queueId: string, agentId: string, updates: Partial<QueueAgent>) =>
        send({ type: 'UPDATE_AGENT', queueId, agentId, updates }),
      removeAgent: (queueId: string, agentId: string) =>
        send({ type: 'REMOVE_AGENT', queueId, agentId }),
      addCaller: (queueId: string, caller: QueueCaller) =>
        send({ type: 'ADD_CALLER', queueId, caller }),
      updateCaller: (queueId: string, callerId: string, updates: Partial<QueueCaller>) =>
        send({ type: 'UPDATE_CALLER', queueId, callerId, updates }),
      removeCaller: (queueId: string, callerId: string) =>
        send({ type: 'REMOVE_CALLER', queueId, callerId }),
      setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
    }
  }, [machineApi])
}

export function useQueueActions() {
  const machineApi = useQueueListMachine()
  const send = machineApi[1]

  return useMemo(() => ({
    setQueues: (queues: Queue[]) => send({ type: 'SET_QUEUES', queues }),
    setSelectedQueue: (queue: Queue | null) => send({ type: 'SET_SELECTED_QUEUE', queue }),
    updateQueue: (id: string, updates: Partial<Queue>) =>
      send({ type: 'UPDATE_QUEUE', id, updates }),
    updateQueueStats: (id: string, stats: QueueStats) =>
      send({ type: 'UPDATE_QUEUE_STATS', id, stats }),
    addAgent: (queueId: string, agent: QueueAgent) => send({ type: 'ADD_AGENT', queueId, agent }),
    updateAgent: (queueId: string, agentId: string, updates: Partial<QueueAgent>) =>
      send({ type: 'UPDATE_AGENT', queueId, agentId, updates }),
    removeAgent: (queueId: string, agentId: string) =>
      send({ type: 'REMOVE_AGENT', queueId, agentId }),
    addCaller: (queueId: string, caller: QueueCaller) =>
      send({ type: 'ADD_CALLER', queueId, caller }),
    updateCaller: (queueId: string, callerId: string, updates: Partial<QueueCaller>) =>
      send({ type: 'UPDATE_CALLER', queueId, callerId, updates }),
    removeCaller: (queueId: string, callerId: string) =>
      send({ type: 'REMOVE_CALLER', queueId, callerId }),
    setLoading: (loading: boolean) => send({ type: 'SET_LOADING', loading }),
  }), [send])
}

export function useQueues() {
  return useQueueListStoreCompat().queues
}
export function useSelectedQueue() {
  return useQueueListStoreCompat().selectedQueue
}
